import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight, ClipboardCheck, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

const CyberEssentialsReadinessService = () => {
  const localAreas = ["Somerset", "North Somerset", "Weston-super-Mare", "Bristol", "South Gloucestershire"];
  
  return (
    <div className="min-h-screen">
      <SEO
        title="Cyber Essentials Readiness Assessment UK | Gap Analysis | Pre-Certification Check | 4FOX"
        description="Cyber Essentials readiness assessment for UK businesses. Identify gaps before certification. Expert guidance on the 5 controls. Get ready to certify with confidence."
        canonical="https://4fox.uk/services/cyber-essentials-readiness"
        localAreas={localAreas}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "ProfessionalService",
              "name": "Cyber Essentials Readiness Assessment",
              "provider": {
                "@type": "Organization",
                "name": "4FOX Security"
              },
              "serviceType": "Cyber Essentials Gap Analysis and Readiness Assessment",
              "areaServed": [
                { "@type": "Country", "name": "United Kingdom" },
                ...localAreas.map(area => ({ "@type": "City", "name": area }))
              ]
            },
            {
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What does the readiness assessment include?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "The readiness assessment includes an initial consultation (30 minutes) to help you understand where you stand. A detailed gap analysis with remediation roadmap is available as part of our certification support package."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How long does a readiness assessment take?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "The initial consultation takes 30 minutes. A comprehensive gap analysis across all five Cyber Essentials controls typically takes 1-2 weeks."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What if I fail the readiness check?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "A readiness assessment isn't pass or fail - it identifies gaps that need addressing before certification. We provide a prioritized remediation roadmap and can support you in fixing the gaps."
                  }
                }
              ]
            }
          ]
        }}
      />
      <Navbar />
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <ClipboardCheck className="w-12 h-12 text-accent" />
              <h1 className="text-4xl md:text-5xl font-bold">Cyber Essentials Readiness Assessment</h1>
            </div>
            
            <p className="text-xl text-muted-foreground mb-8">
              Check if you're ready to certify. Expert initial consultation to identify gaps and create your roadmap to Cyber Essentials certification.
            </p>

            {/* Key Benefit Boxes */}
            <div className="grid md:grid-cols-3 gap-4 mb-12">
              <div className="bg-accent/10 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-accent mb-2">30 Min</div>
                <div className="text-sm text-muted-foreground">Initial Consultation</div>
              </div>
              <div className="bg-accent/10 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-accent mb-2">1-2 Weeks</div>
                <div className="text-sm text-muted-foreground">Full Gap Analysis</div>
              </div>
              <div className="bg-accent/10 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-accent mb-2">Clear Path</div>
                <div className="text-sm text-muted-foreground">To Certification</div>
              </div>
            </div>

            {/* Not Sure If You're Ready */}
            <div className="bg-primary/5 border-l-4 border-accent rounded-lg p-6 mb-8">
              <h2 className="text-2xl font-bold mb-3 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-accent" />
                Not Sure If You're Ready?
              </h2>
              <p className="text-muted-foreground mb-3">
                Many organisations rush into Cyber Essentials certification without understanding their current security posture. This can lead to:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Failed certification attempts (costing time and money)</li>
                <li>• Last-minute scrambling to fix critical gaps</li>
                <li>• Unexpected costs for remediation work</li>
                <li>• Delayed project timelines or lost contract opportunities</li>
              </ul>
            </div>

            <div className="prose prose-lg max-w-none mb-12">
              {/* What is Readiness Assessment */}
              <h2 className="text-2xl font-bold mb-4">What is a Readiness Assessment?</h2>
              <p className="mb-6">
                A Cyber Essentials Readiness Assessment is a comprehensive gap analysis that evaluates your current security controls against the five Cyber Essentials requirements. We identify what's working, what needs improvement, and create a prioritized roadmap to get you certification-ready.
              </p>

              {/* Why Start with Readiness */}
              <h2 className="text-2xl font-bold mb-4">Why Start with Readiness?</h2>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>Avoid failed attempts</strong>
                    <p className="text-muted-foreground text-sm">Identify and fix issues before formal assessment</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>Understand investment required</strong>
                    <p className="text-muted-foreground text-sm">Clear picture of time and resources needed</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>Get expert guidance</strong>
                    <p className="text-muted-foreground text-sm">Leverage our 100+ certification experience</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>Identify quick wins</strong>
                    <p className="text-muted-foreground text-sm">Prioritize easy fixes to fast-track certification</p>
                  </div>
                </div>
              </div>

              {/* Assessment Process */}
              <h2 className="text-2xl font-bold mb-4">Our Readiness Assessment Process</h2>
              <div className="space-y-4 mb-6">
                <div className="bg-secondary/20 rounded-lg p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center text-accent font-bold">1</div>
                    <div>
                      <strong className="text-lg">Initial Consultation (30 mins)</strong>
                      <p className="text-muted-foreground mt-1">Understand your IT environment, business goals, and timeline. Discuss the five Cyber Essentials controls at a high level.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-secondary/20 rounded-lg p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center text-accent font-bold">2</div>
                    <div>
                      <strong className="text-lg">Technical Gap Analysis (1-2 weeks)</strong>
                      <p className="text-muted-foreground mt-1">Deep dive into each of the five controls. Review configurations, policies, and procedures. Interview IT staff and review documentation.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-secondary/20 rounded-lg p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center text-accent font-bold">3</div>
                    <div>
                      <strong className="text-lg">Detailed Report with Recommendations</strong>
                      <p className="text-muted-foreground mt-1">Receive a comprehensive report identifying gaps, risks, and compliance issues. Each finding includes clear remediation steps.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-secondary/20 rounded-lg p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center text-accent font-bold">4</div>
                    <div>
                      <strong className="text-lg">Roadmap to Certification</strong>
                      <p className="text-muted-foreground mt-1">Prioritized action plan with timelines and effort estimates. Clear path from current state to certification.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* The 5 Controls Explained */}
              <h2 className="text-2xl font-bold mb-4">The 5 Cyber Essentials Controls Explained</h2>
              <p className="mb-4">We assess your compliance across all five technical controls:</p>
              <div className="space-y-4 mb-6">
                <div className="bg-secondary/20 rounded-lg p-5">
                  <strong className="text-accent text-lg">1. Firewalls</strong>
                  <p className="text-muted-foreground mt-2">Protect network boundaries and internet-facing services. We check firewall configurations, rule documentation, and boundary controls.</p>
                  <p className="text-sm text-muted-foreground mt-1"><strong>Common issues:</strong> Undocumented rules, overly permissive access, missing personal firewall policies</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-5">
                  <strong className="text-accent text-lg">2. Secure Configuration</strong>
                  <p className="text-muted-foreground mt-2">Ensure systems are hardened and unnecessary services removed. We review OS configurations, application settings, and user privileges.</p>
                  <p className="text-sm text-muted-foreground mt-1"><strong>Common issues:</strong> Default passwords, unnecessary services running, admin accounts used for daily work</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-5">
                  <strong className="text-accent text-lg">3. User Access Control</strong>
                  <p className="text-muted-foreground mt-2">Control who can access your systems and data. We verify authentication mechanisms, password policies, and account management processes.</p>
                  <p className="text-sm text-muted-foreground mt-1"><strong>Common issues:</strong> No MFA, weak password policies, shared accounts, stale user accounts</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-5">
                  <strong className="text-accent text-lg">4. Malware Protection</strong>
                  <p className="text-muted-foreground mt-2">Defend against viruses, ransomware, and other malicious software. We check anti-malware solutions, update status, and scanning configurations.</p>
                  <p className="text-sm text-muted-foreground mt-1"><strong>Common issues:</strong> Outdated signatures, disabled scanning, no mobile device protection</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-5">
                  <strong className="text-accent text-lg">5. Patch Management</strong>
                  <p className="text-muted-foreground mt-2">Keep all systems and software up to date. We assess patch deployment processes, timelines, and coverage across your estate.</p>
                  <p className="text-sm text-muted-foreground mt-1"><strong>Common issues:</strong> No patching schedule, unsupported software, missing critical patches</p>
                </div>
              </div>

              {/* Common Issues We Find */}
              <h2 className="text-2xl font-bold mb-4">Common Issues We Find (and Fix)</h2>
              <p className="mb-4">Based on our experience with 100+ certifications, here's what we typically discover:</p>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong className="text-accent">Somerset SMEs</strong>
                  <ul className="text-muted-foreground text-sm mt-2 space-y-1">
                    <li>• No formal patch management process</li>
                    <li>• Personal devices without MDM</li>
                    <li>• Weak password policies</li>
                  </ul>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong className="text-accent">Bristol Tech Companies</strong>
                  <ul className="text-muted-foreground text-sm mt-2 space-y-1">
                    <li>• Shadow IT and unmanaged SaaS</li>
                    <li>• Missing MFA on critical systems</li>
                    <li>• Incomplete asset inventory</li>
                  </ul>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong className="text-accent">Manufacturing</strong>
                  <ul className="text-muted-foreground text-sm mt-2 space-y-1">
                    <li>• Legacy systems without support</li>
                    <li>• Operational technology not segregated</li>
                    <li>• Insufficient network documentation</li>
                  </ul>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong className="text-accent">Professional Services</strong>
                  <ul className="text-muted-foreground text-sm mt-2 space-y-1">
                    <li>• BYOD without security controls</li>
                    <li>• No user access reviews</li>
                    <li>• Cloud services not configured securely</li>
                  </ul>
                </div>
              </div>

              {/* After Your Assessment */}
              <h2 className="text-2xl font-bold mb-4">After Your Assessment</h2>
              <p className="mb-4">Once we've completed your readiness assessment, you have several options:</p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>DIY with our guidance</strong>
                    <p className="text-muted-foreground text-sm">Use our report to fix gaps in-house and return when ready</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>Full certification support</strong>
                    <p className="text-muted-foreground text-sm">We handle everything from gap remediation to certification</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>Hybrid approach</strong>
                    <p className="text-muted-foreground text-sm">You fix some gaps, we handle the complex technical issues</p>
                  </div>
                </div>
              </div>

              {/* Readiness vs Certification */}
              <h2 className="text-2xl font-bold mb-4">Readiness vs Full Certification Support</h2>
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-secondary/20">
                      <th className="border border-border p-3 text-left">What's Included</th>
                      <th className="border border-border p-3 text-center">Readiness Assessment</th>
                      <th className="border border-border p-3 text-center">Full Certification</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-border p-3">Initial consultation</td>
                      <td className="border border-border p-3 text-center">✓ (Free)</td>
                      <td className="border border-border p-3 text-center">✓</td>
                    </tr>
                    <tr className="bg-secondary/10">
                      <td className="border border-border p-3">Gap analysis across 5 controls</td>
                      <td className="border border-border p-3 text-center">✓</td>
                      <td className="border border-border p-3 text-center">✓</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">Detailed report & roadmap</td>
                      <td className="border border-border p-3 text-center">✓</td>
                      <td className="border border-border p-3 text-center">✓</td>
                    </tr>
                    <tr className="bg-secondary/10">
                      <td className="border border-border p-3">Hands-on remediation support</td>
                      <td className="border border-border p-3 text-center">-</td>
                      <td className="border border-border p-3 text-center">✓</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">Application completion</td>
                      <td className="border border-border p-3 text-center">-</td>
                      <td className="border border-border p-3 text-center">✓</td>
                    </tr>
                    <tr className="bg-secondary/10">
                      <td className="border border-border p-3">Submission & certification</td>
                      <td className="border border-border p-3 text-center">-</td>
                      <td className="border border-border p-3 text-center">✓</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* FAQ Section */}
              <h2 className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
              <div className="space-y-4 mb-6">
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>Is the readiness assessment free?</strong>
                  <p className="text-muted-foreground text-sm mt-2">Yes, we offer a free initial readiness consultation (30 minutes) to help you understand where you stand. A detailed gap analysis with remediation roadmap is available as part of our certification support package.</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>How long does a readiness assessment take?</strong>
                  <p className="text-muted-foreground text-sm mt-2">The initial consultation takes 30 minutes. A comprehensive gap analysis across all five Cyber Essentials controls typically takes 1-2 weeks, depending on the size and complexity of your IT environment.</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>What if I fail the readiness check?</strong>
                  <p className="text-muted-foreground text-sm mt-2">A readiness assessment isn't pass or fail - it's designed to identify gaps that need addressing before certification. We provide a prioritized remediation roadmap and can support you in fixing the gaps we identify.</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>Can you fix the gaps you find?</strong>
                  <p className="text-muted-foreground text-sm mt-2">Absolutely. We offer hands-on remediation support as part of our full certification service. We can fix everything from patch management automation to firewall configurations.</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>How much does it cost to fix gaps?</strong>
                  <p className="text-muted-foreground text-sm mt-2">It depends on what gaps we find. Some fixes are quick (enabling MFA), others require more work (implementing MDM for mobile devices). We provide cost estimates in our remediation roadmap.</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>When should I get a readiness assessment?</strong>
                  <p className="text-muted-foreground text-sm mt-2">As early as possible! If you're bidding for a contract that requires Cyber Essentials, get assessed 2-3 months before you need certification to allow time for fixing gaps.</p>
                </div>
              </div>

              {/* Local Focus */}
              <div className="bg-accent/10 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-2">Serving Somerset, Bristol & the UK</h3>
                <p className="text-muted-foreground">
                  We provide Cyber Essentials readiness assessments for businesses across Somerset, North Somerset, Weston-super-Mare, Bristol, South Gloucestershire, and throughout the United Kingdom.
                </p>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-secondary/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-4">Check Your Readiness Today</h3>
              <p className="text-muted-foreground mb-6">
                Book a free 30-minute consultation to understand where you stand and what you need to do to achieve Cyber Essentials certification.
              </p>
              <div className="flex flex-wrap gap-4">
                <a 
                  href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button size="lg" className="bg-accent hover:bg-accent/90">
                    Book Free Readiness Call
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <Link to="/services/cyber-essentials-certification-uk">
                  <Button size="lg" variant="outline">
                    Learn About Certification
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CyberEssentialsReadinessService;